import React from 'react';
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';

const tooltipStyle = {
  background: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  fontSize: '.78rem',
  color: 'var(--text)',
};

const SIGNAL_BUCKETS = [
  { name: '> -20', tone: 'var(--success)', test: v => v > -20 },
  { name: '-20 to -24', tone: 'var(--success)', test: v => v > -24 },
  { name: '-24 to -27', tone: 'var(--warning)', test: v => v > -27 },
  { name: '< -27', tone: 'var(--danger)', test: () => true },
];

export function HealthDonut({ healthy, weak, offline }) {
  const total = healthy + weak + offline;
  if (!total) return null;
  const data = [
    { name: 'Healthy', value: healthy, color: 'var(--success)' },
    { name: 'Weak signal', value: weak, color: 'var(--warning)' },
    { name: 'Offline', value: offline, color: 'var(--danger)' },
  ].filter(d => d.value > 0);

  return (
    <div className="ow-donut">
      <ResponsiveContainer width="100%" height={168}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" innerRadius={52} outerRadius={74} paddingAngle={2} stroke="none">
            {data.map(d => <Cell key={d.name} fill={d.color} />)}
          </Pie>
          <Tooltip contentStyle={tooltipStyle} itemStyle={{ color: 'var(--text)' }} />
        </PieChart>
      </ResponsiveContainer>
      <div className="ow-donut-center">
        <b>{total}</b>
        <span>ONUs</span>
      </div>
      <div className="ow-legend">
        {data.map(d => (
          <span key={d.name}>
            <i style={{ background: d.color }} />
            {d.name} <b>{d.value}</b>
          </span>
        ))}
      </div>
    </div>
  );
}

export function PonUsageChart({ portStats }) {
  const data = Object.keys(portStats).map(Number).sort((a, b) => a - b).map(p => ({
    name: `PON ${p}`,
    online: portStats[p].online,
    offline: Math.max(portStats[p].total - portStats[p].online, 0),
  }));
  if (!data.length) return <p className="ow-empty-note">No PON data yet.</p>;
  const height = Math.max(150, data.length * 30 + 18);

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 14, left: 0, bottom: 0 }} barSize={12}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
        <XAxis type="number" allowDecimals={false} tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
        <YAxis type="category" dataKey="name" width={56} tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
        <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'var(--bg)' }} />
        <Bar dataKey="online" name="Online" stackId="a" fill="var(--success)" />
        <Bar dataKey="offline" name="Offline" stackId="a" fill="var(--danger)" radius={[0, 4, 4, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function SignalHistogram({ onus }) {
  const counts = SIGNAL_BUCKETS.map(() => 0);
  let noReading = 0;

  for (const o of onus) {
    const online = (o.status || '').toLowerCase().includes('online') || o.online === true;
    if (!online) continue;
    const v = parseFloat(o.rxPower ?? o.rx_power ?? o.opticalRx);
    if (!Number.isFinite(v)) { noReading++; continue; }
    const index = SIGNAL_BUCKETS.findIndex(b => b.test(v));
    if (index >= 0) counts[index]++;
  }

  const data = SIGNAL_BUCKETS.map((b, i) => ({ name: b.name, count: counts[i], tone: b.tone }));
  if (noReading) data.push({ name: 'No reading', count: noReading, tone: 'var(--text-muted)' });
  if (!data.some(d => d.count > 0)) return <p className="ow-empty-note">No online ONUs to show.</p>;

  return (
    <ResponsiveContainer width="100%" height={168}>
      <BarChart data={data} margin={{ top: 8, right: 6, left: -18, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
        <XAxis dataKey="name" tick={{ fill: 'var(--text-muted)', fontSize: 10 }} axisLine={false} tickLine={false} interval={0} />
        <YAxis allowDecimals={false} tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
        <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'var(--bg)' }} />
        <Bar dataKey="count" name="ONUs" radius={[4, 4, 0, 0]}>
          {data.map(d => <Cell key={d.name} fill={d.tone} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
